import * as Tone from 'tone';

class AudioManager {
    constructor() {
        this.isInitialized = false;
        this.engineSound = null;
        this.tiltingSound = null;

        // A low-pass filter to make the engine sound more "aggressive" by opening it up
        this.engineFilter = new Tone.Filter({
            type: 'lowpass',
            frequency: 400, // Start with a muffled sound
            Q: 1,
        }).toDestination();

        // A panner to handle the left-right tilting sound
        this.tiltPanner = new Tone.Panner(0).connect(this.engineFilter);
    }

    // Call this from a user interaction, like the "start game" button
    async init() {
        if (this.isInitialized) return;
        await Tone.start();
        console.log("Audio context started.");

        // --- Create the Engine Sound ---
        // A combination of a low-frequency hum and some noise for texture
        const engineHum = new Tone.Oscillator({
            type: 'sawtooth', // A buzzy, rich waveform
            frequency: 60,   // Low base frequency
        }).connect(this.tiltPanner);

        const engineNoise = new Tone.Noise({
            type: 'brown', // 'pink' or 'brown' noise sounds more natural for engines than 'white'
            volume: -15,   // Quieter than the main hum
        }).connect(this.tiltPanner);

        this.engineSound = { hum: engineHum, noise: engineNoise };

        this.jumpSynth = new Tone.Synth({
            oscillator: { type: 'triangle' },
            envelope: {
                attack: 0.005,
                decay: 0.1,
                sustain: 0.2,
                release: 0.2
            },
            volume: -6 // Set volume to be clearly audible
        }).toDestination();


        // --- Create Land Sound ---
        // A short burst of filtered noise to create a "thud" sound.
        this.landSynth = new Tone.NoiseSynth({
            noise: { type: 'pink' },
            envelope: {
                attack: 0.001,
                decay: 0.15,
                sustain: 0,
            },
            volume: -5
        }).toDestination();

        this.isInitialized = true;
    }

    startEngine() {
        if (!this.isInitialized || !this.engineSound) return;
        //this.engineSound.hum.start();
        this.engineSound.noise.start();
    }

    stopEngine() {
        if (!this.isInitialized || !this.engineSound) return;
        try { this.engineSound.hum.stop(); } catch (e) { /* already stopped */ }
        try { this.engineSound.noise.stop(); } catch (e) { /* already stopped */ }
    }

    dispose() {
        if (!this.isInitialized) return;
        this.stopEngine();
        [
            this.engineSound?.hum,
            this.engineSound?.noise,
            this.jumpSynth,
            this.landSynth,
            this.tiltPanner,
            this.engineFilter,
        ].forEach((node) => {
            if (node && typeof node.dispose === 'function') node.dispose();
        });
        this.engineSound = null;
        this.jumpSynth = null;
        this.landSynth = null;
        this.isInitialized = false;
    }

    /**
     * Update engine sound based on player speed.
     * @param {number} speed - A normalized speed value (e.g., 0.0 to 1.0)
     */
    updateEngineSpeed(speed) {
        if (!this.isInitialized || !this.engineSound) return;

        // Clamp the speed value to avoid extreme frequencies
        const normalizedSpeed = Math.max(0, Math.min(1, speed));

        // As speed increases, the pitch gets higher
        const baseFreq = 20;
        const maxFreq = 120;
        this.engineSound.hum.frequency.rampTo(baseFreq + (maxFreq - baseFreq) * normalizedSpeed, 0.1);

        // As speed increases, the sound gets less muffled (more "aggressive")
        const minFilterFreq = 400;
        const maxFilterFreq = 2500;
        this.engineFilter.frequency.rampTo(minFilterFreq + (maxFilterFreq - minFilterFreq) * normalizedSpeed, 0.1);
    }

    /**
     * Update the continuous tilting sound.
     * @param {number} tilt - A value from -1 (full left) to 1 (full right)
     */
    updateTilt(tilt) {
        if (!this.isInitialized) return;

        // Pan the entire engine sound left or right
        this.tiltPanner.pan.rampTo(tilt * 0.5, 1);

        // Optional: Add a subtle "stress" sound when tilting hard
        // This is a placeholder for a potential additional sound
    }

    playJumpSound() {
        if (!this.isInitialized || !this.jumpSynth) return;
        // Play a short note that quickly ramps up in pitch.
        this.jumpSynth.triggerAttackRelease("C4", "8n");
        this.jumpSynth.frequency.rampTo("G4", 0.1);
    }

    // --- NEW METHOD: Play Land Sound ---
    playLandSound() {
        if (!this.isInitialized || !this.landSynth) return;
        // Play a very short burst of noise.
        this.landSynth.triggerAttackRelease("0.1");
    }
} export default AudioManager;